import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Modal, Spinner, Table } from 'react-bootstrap';
import { CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { qcResultAPI } from '../../services';
import QcHandlingModal from './QcHandlingModal';
import { QC_STATUS_LABELS, QC_STATUS_VARIANTS, RESOLUTION_LABELS, SOURCE_KIND_LABELS, SOURCE_KIND_VARIANTS } from './QcResultsListTab';

const formatDateTime = (v) => {
  if (!v) return '-';
  try {
    return format(new Date(v), 'dd/MM/yyyy HH:mm');
  } catch {
    return '-';
  }
};

function EvidenceGroup({ title, evidence, onPreview }) {
  if (!evidence) return null;
  const hasAny = evidence.image_urls?.length || evidence.video_urls?.length || evidence.doc_url;
  if (!hasAny) return null;
  return (
    <div className="mb-3">
      <strong>{title}:</strong>
      <div className="d-flex flex-wrap gap-2 mt-2">
        {(evidence.image_urls || []).map((url, idx) => (
          <button key={idx} type="button" className="btn p-0 border-0" onClick={() => onPreview(url)}>
            <img src={url} alt={`evidence-${idx}`} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4 }} />
          </button>
        ))}
        {(evidence.video_urls || []).map((url, idx) => (
          <video key={idx} controls style={{ width: 200, maxHeight: 150, borderRadius: 4 }}>
            <source src={url} type="video/mp4" />
          </video>
        ))}
        {evidence.doc_url && (
          <a href={evidence.doc_url} target="_blank" rel="noopener noreferrer">
            Xem form
          </a>
        )}
      </div>
    </div>
  );
}

function CriteriaList({ criteria }) {
  if (!criteria || criteria.length === 0) return null;
  return (
    <div className="mb-3">
      <strong>Tiêu chí:</strong>
      <ul className="list-unstyled mt-2 mb-0">
        {criteria.map((c) => (
          <li key={c.code} className="d-flex align-items-center gap-2 mb-1">
            {c.passed ? <CheckCircle size={16} className="text-success" /> : <XCircle size={16} className="text-danger" />}
            <span>{c.label}</span>
            {c.is_critical && <Badge bg="warning" text="dark">Quan trọng</Badge>}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function QcResultDetailModal({ sourceKind, id, onHide, onChanged }) {
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showHandling, setShowHandling] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await qcResultAPI.getQcResultDetail(sourceKind, id);
      setRow(data);
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Không tải được chi tiết kết quả QC');
    } finally {
      setLoading(false);
    }
  }, [sourceKind, id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleHandlingSuccess = () => {
    setShowHandling(false);
    load();
    if (onChanged) onChanged();
  };

  return (
    <>
      <Modal show onHide={onHide} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Chi tiết kết quả QC</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading || !row ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <h5 className="mb-1">{row.title}</h5>
                  {row.subtitle && <div className="text-muted small">{row.subtitle}</div>}
                </div>
                <div className="d-flex gap-2">
                  <Badge bg={SOURCE_KIND_VARIANTS[row.source_kind] || 'secondary'}>{SOURCE_KIND_LABELS[row.source_kind] || row.source_kind}</Badge>
                  <Badge bg={QC_STATUS_VARIANTS[row.qc_status] || 'secondary'}>{QC_STATUS_LABELS[row.qc_status] || row.qc_status}</Badge>
                </div>
              </div>

              <Table borderless size="sm" className="mb-3">
                <tbody>
                  <tr>
                    <td className="text-muted" style={{ width: 180 }}>{row.worker_role_label}</td>
                    <td>{row.worker?.full_name || 'Không xác định'}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">QC Reviewer</td>
                    <td>{row.qc_reviewer?.full_name || '-'}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Chi nhánh</td>
                    <td>{row.branch_name || '-'}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Thời gian QC</td>
                    <td>{formatDateTime(row.qc_reviewed_at)}</td>
                  </tr>
                  {row.qc_session && (
                    <tr>
                      <td className="text-muted">Phiên QC</td>
                      <td>
                        {row.qc_session.planned_task_date}
                        {row.qc_session.qc_employee_name ? ` · ${row.qc_session.qc_employee_name}` : ''}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {row.qc_review_notes && (
                <div className="mb-3">
                  <strong>Ghi chú QC:</strong>
                  <div className="text-muted">{row.qc_review_notes}</div>
                </div>
              )}

              <EvidenceGroup title="Bằng chứng nhân viên" evidence={row.worker_evidence} onPreview={setPreviewUrl} />
              <EvidenceGroup title="Bằng chứng QC" evidence={row.qc_evidence} onPreview={setPreviewUrl} />
              <CriteriaList criteria={row.criteria} />

              <hr />
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>Trạng thái xử lý: </strong>
                  {row.handling?.is_handled ? (
                    <Badge bg="success">{RESOLUTION_LABELS[row.handling.resolution] || 'Đã xử lý'}</Badge>
                  ) : (
                    <Badge bg="light" text="dark">Chưa xử lý</Badge>
                  )}
                  {row.handling?.note && <div className="text-muted small mt-1">{row.handling.note}</div>}
                  {row.handling?.point && (
                    <div className="small mt-1">
                      Điểm: <strong>{row.handling.point.type === 'ADD' ? '+' : '-'}{row.handling.point.value}</strong> — {row.handling.point.reason}
                    </div>
                  )}
                  {row.handling?.is_stale && (
                    <div className="text-warning small mt-1">⚠ Kết quả này đã được QC lại sau khi xử lý — cân nhắc xem lại.</div>
                  )}
                </div>
                <Button variant="primary" size="sm" onClick={() => setShowHandling(true)}>
                  Xử lý / Gắn điểm
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Đóng</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!previewUrl} onHide={() => setPreviewUrl(null)} size="lg" centered>
        <Modal.Body className="text-center">
          {previewUrl && <img src={previewUrl} alt="preview" style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />}
        </Modal.Body>
      </Modal>

      {row && (
        <QcHandlingModal
          show={showHandling}
          onHide={() => setShowHandling(false)}
          sourceKind={sourceKind}
          id={id}
          row={row}
          onSuccess={handleHandlingSuccess}
        />
      )}
    </>
  );
}
